import { useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { Route, useParams } from 'react-router-dom'
import styled from 'styled-components/macro'

import { saveLocationFormValues } from '../../redux/locationSlice'
import { saveReviewFormValues } from '../../redux/reviewSlice'
import { useAppHistory } from '../../utils/useAppHistory'
import SettingsButton from '../desktop/SettingsButton'
import { BackButton } from '../ui/ActionButtons'
import ReturnIcon from '../ui/ReturnIcon'
import TopBar from '../ui/TopBar'
import TopBarNav from '../ui/TopBarNav'
import { EditReviewForm } from './EditReview'
import { LocationForm } from './LocationForm'
import { ReviewForm } from './ReviewForm'

const StyledNavBack = styled.div`
  padding-block: 25px 15px;
  padding-inline: 10px 15px;
  display: flex;
  justify-content: space-between;
`

const Header = styled.h3`
  margin-inline-start: 10px;
`

const MobileNav = ({ title, onBack }) => (
  <TopBar>
    <TopBarNav onBack={onBack} title={title} />
  </TopBar>
)

const DesktopNav = ({ title, onBack }) => {
  const { t } = useTranslation()
  return (
    <>
      <StyledNavBack>
        <BackButton onClick={onBack}>
          <ReturnIcon />
          {t('layouts.back')}
        </BackButton>
      </StyledNavBack>
      <Header>{title}</Header>
    </>
  )
}

const DesktopButtonUnderForm = ({ formRef, saveFormValues }) => {
  const dispatch = useDispatch()
  const history = useAppHistory()

  const handleClick = () => {
    if (formRef.current) {
      dispatch(saveFormValues(formRef.current.values))
    }
    history.push('/settings')
  }

  return <SettingsButton onClick={handleClick} />
}

const EditLocation = ({ NavComponent, withSettingsButton }) => {
  const history = useAppHistory()
  const formRef = useRef()
  const { locationId } = useParams()
  const { t } = useTranslation()
  const streetViewOpen = useSelector((state) => state.panorama.streetViewOpen)

  return (
    <>
      {!streetViewOpen && (
        <NavComponent
          title={t('menu.edit_location')}
          onBack={(event) => {
            event.stopPropagation()
            history.push(`/locations/${locationId}`)
          }}
        />
      )}
      <LocationForm editingId={Number(locationId)} innerRef={formRef} />
      {withSettingsButton && (
        <DesktopButtonUnderForm
          formRef={formRef}
          saveFormValues={saveLocationFormValues}
        />
      )}
    </>
  )
}

const AddLocation = ({ NavComponent, backUrl, withSettingsButton }) => {
  const history = useAppHistory()
  const formRef = useRef()
  const dispatch = useDispatch()
  const { t } = useTranslation()

  return (
    <>
      <NavComponent
        title={t('menu.add_new_location')}
        onBack={(event) => {
          event.stopPropagation()
          if (formRef.current) {
            dispatch(saveLocationFormValues(formRef.current.values))
          }
          history.push(backUrl)
        }}
      />
      <LocationForm innerRef={formRef} />
      {withSettingsButton && (
        <DesktopButtonUnderForm
          formRef={formRef}
          saveFormValues={saveLocationFormValues}
        />
      )}
    </>
  )
}

const AddReview = ({ NavComponent, withSettingsButton }) => {
  const history = useAppHistory()
  const formRef = useRef()
  const { locationId } = useParams()
  const { t } = useTranslation()

  return (
    <>
      <NavComponent
        title={t('menu.add_review')}
        onBack={(event) => {
          event.stopPropagation()
          history.push(`/locations/${locationId}`)
        }}
      />
      <ReviewForm innerRef={formRef} />
      {withSettingsButton && (
        <DesktopButtonUnderForm
          formRef={formRef}
          saveFormValues={saveReviewFormValues}
        />
      )}
    </>
  )
}

const EditReview = ({ NavComponent, withSettingsButton }) => {
  const history = useAppHistory()
  const formRef = useRef()
  const { review } = useSelector((state) => state.review)
  const { t } = useTranslation()

  return (
    <>
      <NavComponent
        title={t('menu.edit_review')}
        onBack={(event) => {
          event.stopPropagation()
          history.push(`/locations/${review?.location_id}`)
        }}
      />
      <EditReviewForm innerRef={formRef} />
      {withSettingsButton && (
        <DesktopButtonUnderForm
          formRef={formRef}
          saveFormValues={saveReviewFormValues}
        />
      )}
    </>
  )
}

export const formRoutesMobile = [
  <Route key="edit-location" path="/locations/:locationId/edit">
    <EditLocation NavComponent={MobileNav} />
  </Route>,
  <Route key="add-location" path="/locations/new">
    <AddLocation NavComponent={MobileNav} backUrl="/locations/init" />
  </Route>,
  <Route key="add-review" path="/locations/:locationId/review">
    <AddReview NavComponent={MobileNav} />
  </Route>,
  <Route key="edit-review" path="/reviews/:reviewId/edit">
    <EditReview NavComponent={MobileNav} />
  </Route>,
]

export const formRoutesDesktop = [
  <Route key="edit-location" path="/locations/:locationId/edit">
    <EditLocation NavComponent={DesktopNav} withSettingsButton />
  </Route>,
  <Route key="add-location" path="/locations/new">
    <AddLocation NavComponent={DesktopNav} backUrl="/map" withSettingsButton />
  </Route>,
  <Route key="add-review" path="/locations/:locationId/review">
    <AddReview NavComponent={DesktopNav} withSettingsButton />
  </Route>,
  <Route key="edit-review" path="/reviews/:reviewId/edit">
    <EditReview NavComponent={DesktopNav} withSettingsButton />
  </Route>,
]
